import type { QueryClient } from '@tanstack/react-query';
import {
  LLMExtract,
  fetchTemplateById,
  renderTemplate,
  setTemplatePlugins,
} from '@/api';
import { safeAsync } from '@morph-mapper/utils';
import {
  PluginFrameworkErrorType,
  type PluginHostApi,
} from '@morph-mapper/plugins';
import { err, ok } from 'neverthrow';
import { useStore } from '@/store';

export const createPluginHostApi = (
  queryClient: QueryClient,
): PluginHostApi => ({
  createCallRender: (textContent) => async (code) => {
    const variant = useStore.getState().config.getVariant();
    const result = await safeAsync(() =>
      renderTemplate(
        variant,
        JSON.parse(`{"value": ${code}}`),
        textContent,
        {},
      ),
    );

    if (result.isErr()) {
      return err({
        type: PluginFrameworkErrorType.CORRUPTED_STATE,
        message: result.error.message,
      });
    }

    if (!result.value?.output) {
      return err({
        type: PluginFrameworkErrorType.CORRUPTED_STATE,
        message: 'renderTemplate returned empty output',
      });
    }

    const output = Array.isArray(result.value.output)
      ? result.value.output[0]
      : result.value.output;

    if (!output) {
      return err({
        type: PluginFrameworkErrorType.CORRUPTED_STATE,
        message: 'renderTemplate returned empty output after normalization',
      });
    }

    return ok(output.value);
  },

  createGetTemplate: () => async (id) => {
    const result = await safeAsync(() =>
      queryClient.fetchQuery({
        queryKey: ['template', id],
        queryFn: () => fetchTemplateById(id),
      }),
    );

    if (result.isErr()) {
      return err({
        type: PluginFrameworkErrorType.CORRUPTED_STATE,
        message: result.error.message,
      });
    }

    return ok(result.value);
  },

  createLLMExtract: () => async (prompt, promptName, title) => {
    const jwt = useStore.getState().config.getJWT();

    const result = await safeAsync(() =>
      LLMExtract(prompt, promptName, jwt, title),
    );

    if (result.isErr()) {
      return err({
        type: PluginFrameworkErrorType.CORRUPTED_STATE,
        message: result.error.message,
      });
    }

    return ok(result.value);
  },

  createSetTemplatePlugins: () => async (id, pluginData) => {
    const result = await safeAsync(() => setTemplatePlugins(id, pluginData));

    if (result.isErr()) {
      return err({
        type: PluginFrameworkErrorType.CORRUPTED_STATE,
        message: result.error.message,
      });
    }

    await queryClient.invalidateQueries({ queryKey: ['template', id] });
    await queryClient.invalidateQueries({ queryKey: ['templates'] });

    return ok(undefined);
  },
});
